import { Send } from '@mui/icons-material';
import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton } from '~/components/button';
import { mergeGridActionProps, type GridActionCellProps } from '~/components/table';
import { useUniqueId } from '~/hooks/unique-id';
import { TriggerViewDialog } from '../../components';
import { capabilitiesFor } from '../../lib/capabilities';
import type { DeviceList__Device } from '../../queries/list.generated';
import { CommandPopover, type DeviceCommandHandlers } from './CommandPopover';

export type RowCommandButtonProps = DeviceCommandHandlers &
  GridActionCellProps & {
    device: DeviceList__Device;
  };

export const RowCommandButton = ({
  device,
  onClick,
  role,
  tabIndex,
  touchRippleRef,
  ...handlers
}: RowCommandButtonProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [triggerViewOpen, setTriggerViewOpen] = useState(false);
  const close = useCallback(() => setAnchorEl(null), []);
  const buttonId = useUniqueId('send-command-button');
  const menuId = useUniqueId('send-command-menu');

  const selected = useMemo(() => ({ ids: [device.id] }), [device.id]);

  const caps = capabilitiesFor(device.kind);

  // List query has activeShow + channelGuide so we can gate tune_channel and
  // play_view here. realtime/canUpdateSoftware/metadata aren't in the list
  // query, so play/stop overlay, update_software, and install_firmware can't
  // be gated per-row. Kind-based items are shown disabled with a tooltip
  // instead of hidden, so the action menu stays consistent.
  const disabledItems = useMemo<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    const notSupported = t('pages.devices.bulk_actions.disabled_reasons.not_supported');
    if (!caps.brightness) result.set_brightness = notSupported;
    if (!caps.absoluteVolume) result.set_volume = notSupported;
    if (!caps.analyzeChannels) result.analyze_channels = notSupported;
    if (!caps.physicalKeys) {
      result.keys_lock = notSupported;
      result.keys_unlock = notSupported;
    }
    if (!caps.remote) {
      result.remote_lock = notSupported;
      result.remote_unlock = notSupported;
    }
    if (!device.activeShow?.show.channelGuide?.channelCount) {
      result.tune_channel = t('pages.devices.bulk_actions.disabled_reasons.no_channel_guide');
    }
    if (!device.activeShow) {
      result.play_view = t('pages.devices.bulk_actions.disabled_reasons.no_active_show');
    } else if (device.activeShow.show.viewCount < 2) {
      result.play_view = t('pages.devices.bulk_actions.disabled_reasons.single_view_show');
    }
    return result;
  }, [caps, device.activeShow, t]);

  const buttonProps = mergeGridActionProps(
    { onClick: (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget) },
    { onClick, role, tabIndex, touchRippleRef },
  );

  return (
    <>
      <ActionButton
        aria-controls={anchorEl ? menuId : undefined}
        aria-expanded={anchorEl ? 'true' : undefined}
        aria-haspopup="true"
        id={buttonId}
        title={t('pages.devices.list.actions.send_command')}
        Icon={Send}
        {...buttonProps}
      />
      <CommandPopover
        {...handlers}
        onTriggerView={() => setTriggerViewOpen(true)}
        anchorEl={anchorEl}
        disabledItems={disabledItems}
        labelledBy={buttonId}
        menuId={menuId}
        open={Boolean(anchorEl)}
        onClose={close}
        selected={selected}
        subject={`${device.name} (${device.id})`}
      />
      {triggerViewOpen && device.activeShow && (
        <TriggerViewDialog
          activeShowId={device.activeShow.show.id}
          onClose={() => setTriggerViewOpen(false)}
          selected={selected}
        />
      )}
    </>
  );
};
